import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Undo2, Redo2, Loader2 } from "lucide-react";

/**
 * Global Undo / Redo action stack — client-side.
 *
 * The user wanted INSTANT, single-action rollback (no page reload, no DB
 * snapshot churn). We store CLIENT-SIDE entries:
 *
 *   { label, undo: async () => void, redo: async () => void }
 *
 * Anywhere a meaningful change happens (delete row, edit a field, change a
 * status etc.) the calling component calls `pushUndo({ label, undo, redo })`.
 *
 *  - `undo` brings the state back to BEFORE the change.
 *  - `redo` re-applies the change after an `undo`.
 *
 * Legacy form `{ label, restore }` is still accepted — it maps to a one-shot
 * undo with no redo (the existing delete callsites keep working).
 *
 * Stacks are reset on route change so callbacks never reference stale state.
 */

const MAX_DEPTH = 100;
const UndoContext = createContext(null);

export function UndoProvider({ children }) {
  // past = actions that CAN be undone (top of stack = most recent).
  // future = actions that were undone and can be redone (top = most recent undo).
  const pastRef = useRef([]);
  const futureRef = useRef([]);
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const location = useLocation();

  const bump = () => setVersion((v) => v + 1);

  // Whenever the route path changes, clear the stack — entries reference
  // setters/state local to the previous page and won't work elsewhere.
  useEffect(() => {
    pastRef.current = [];
    futureRef.current = [];
    bump();
  }, [location.pathname]);

  const pushUndo = useCallback((entry) => {
    if (!entry) return;
    // Accept both new ({undo, redo}) and legacy ({restore}) shapes.
    const undoFn = entry.undo || entry.restore;
    if (typeof undoFn !== "function") return;
    pastRef.current.push({
      label: entry.label || "פעולה",
      undo: undoFn,
      redo: typeof entry.redo === "function" ? entry.redo : null,
    });
    if (pastRef.current.length > MAX_DEPTH) pastRef.current.shift();
    // A new action invalidates the redo branch.
    futureRef.current = [];
    bump();
  }, []);

  const clearUndo = useCallback(() => {
    pastRef.current = [];
    futureRef.current = [];
    bump();
  }, []);

  const undo = useCallback(async () => {
    const entry = pastRef.current[pastRef.current.length - 1];
    if (!entry || busy) return;
    setBusy(true);
    try {
      await entry.undo();
      pastRef.current.pop();
      // Only entries WITH a redo function can be redone — legacy `restore`
      // entries are one-shot.
      if (entry.redo) futureRef.current.push(entry);
      bump();
      toast.success(`בוטל: ${entry.label}`);
    } catch (e) {
      toast.error(`ביטול נכשל: ${e?.response?.data?.detail || e?.message || "שגיאה"}`);
    } finally {
      setBusy(false);
    }
  }, [busy]);

  const redo = useCallback(async () => {
    const entry = futureRef.current[futureRef.current.length - 1];
    if (!entry || !entry.redo || busy) return;
    setBusy(true);
    try {
      await entry.redo();
      futureRef.current.pop();
      pastRef.current.push(entry);
      bump();
      toast.success(`הוחזר: ${entry.label}`);
    } catch (e) {
      toast.error(`החזרה נכשלה: ${e?.response?.data?.detail || e?.message || "שגיאה"}`);
    } finally {
      setBusy(false);
    }
  }, [busy]);

  // Keyboard shortcuts: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Y or Ctrl/Cmd+Shift+Z = redo.
  // Ignore when typing inside an input/textarea/contenteditable so we don't
  // hijack the user's native field-level undo (typing chars).
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const t = e.target;
      const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || t?.isContentEditable) return;
      const k = e.key.toLowerCase();
      if (k === "z" && !e.shiftKey) { e.preventDefault(); undo(); }
      else if ((k === "z" && e.shiftKey) || k === "y") { e.preventDefault(); redo(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [undo, redo]);

  const topPast = pastRef.current[pastRef.current.length - 1];
  const topFuture = futureRef.current[futureRef.current.length - 1];

  return (
    <UndoContext.Provider value={{
      pushUndo, undo, redo, clearUndo,
      depth: pastRef.current.length,
      redoDepth: futureRef.current.length,
      lastLabel: topPast?.label || "",
      nextLabel: topFuture?.label || "",
      busy,
      version,
    }}>
      {children}
    </UndoContext.Provider>
  );
}

export function useUndo() {
  const ctx = useContext(UndoContext);
  if (!ctx) {
    return {
      pushUndo: () => {}, undo: () => {}, redo: () => {}, clearUndo: () => {},
      depth: 0, redoDepth: 0, lastLabel: "", nextLabel: "", busy: false,
    };
  }
  return ctx;
}

/**
 * Helper hook for "field-level" undo. Wire it inside any controlled
 * input via `onBlur` (or after a debounced save). Pass the field's
 * previous & new values + sync functions to apply each.
 *
 *   const trackField = useFieldUndo();
 *   onBlur={() => trackField({
 *     label: `שדה "שם"`,
 *     prev: prevValue,
 *     next: currentValue,
 *     apply: async (val) => { setLocal(val); await api.patch(...); },
 *   })}
 *
 * If prev === next, nothing is pushed.
 */
export function useFieldUndo() {
  const { pushUndo } = useUndo();
  return useCallback(({ label, prev, next, apply }) => {
    if (prev === next) return;
    if (typeof apply !== "function") return;
    pushUndo({
      label,
      undo: () => apply(prev),
      redo: () => apply(next),
    });
  }, [pushUndo]);
}

/** Compact circular pair: Undo (left) + Redo (right) — for top-left FAB.
 *  Replaces the heavy snapshot-based controls. Instant, no reloads. */
export function UndoRedoFab() {
  const { undo, redo, depth, redoDepth, lastLabel, nextLabel, busy } = useUndo();
  const undoDisabled = depth === 0 || busy;
  const redoDisabled = redoDepth === 0 || busy;
  return (
    <div className="inline-flex flex-col items-center gap-1" data-testid="dashboard-undo-redo-controls">
      <button
        type="button"
        onClick={undo}
        disabled={undoDisabled}
        className="relative h-9 w-9 rounded-full bg-stone-900/85 hover:bg-pink-600/40 border border-stone-700 hover:border-pink-400/60 text-stone-300 hover:text-pink-100 transition disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center shadow-lg"
        data-testid="dashboard-undo-btn"
        title={undoDisabled ? "אין פעולה לבטל (Ctrl+Z)" : `בטל: ${lastLabel} (Ctrl+Z)`}
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Undo2 className="w-4 h-4" />}
        {depth > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-pink-600 text-white text-[10px] font-extrabold inline-flex items-center justify-center shadow">
            {depth}
          </span>
        )}
      </button>
      <button
        type="button"
        onClick={redo}
        disabled={redoDisabled}
        className="relative h-9 w-9 rounded-full bg-stone-900/85 hover:bg-emerald-600/40 border border-stone-700 hover:border-emerald-400/60 text-stone-300 hover:text-emerald-100 transition disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center justify-center shadow-lg"
        data-testid="dashboard-redo-btn"
        title={redoDisabled ? "אין פעולה להחזיר (Ctrl+Y)" : `החזר: ${nextLabel} (Ctrl+Y)`}
      >
        <Redo2 className="w-4 h-4" />
        {redoDepth > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-emerald-600 text-white text-[10px] font-extrabold inline-flex items-center justify-center shadow">
            {redoDepth}
          </span>
        )}
      </button>
    </div>
  );
}

/** Legacy single Undo button used inside FullFrameProduction's top bar. */
export function UndoButton() {
  const { undo, depth, lastLabel, busy } = useUndo();
  const disabled = depth === 0 || busy;
  return (
    <button
      type="button"
      onClick={undo}
      disabled={disabled}
      className={`fixed top-3 left-3 z-[9998] inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-sm font-bold shadow-lg transition ${disabled
        ? "bg-stone-800 border-white/10 text-stone-500 cursor-not-allowed"
        : "bg-pink-600 border-pink-400/60 text-white hover:bg-pink-500 shadow-pink-500/30"}`}
      data-testid="ff-undo-btn"
      title={disabled ? "אין פעולות לביטול" : `בטל: ${lastLabel}`}
    >
      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />}
      <span className="hidden sm:inline">חזור</span>
      {depth > 0 && (
        <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white/20 text-[10px] font-extrabold">
          {depth}
        </span>
      )}
    </button>
  );
}
